import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const DirectSellingHero = () => {
  // Timer state
  const [timeLeft, setTimeLeft] = useState({
    days: 3,
    hours: 23,
    minutes: 59,
    seconds: 59
  });

  // Update countdown timer every second
  useEffect(() => {
    const timerInterval = setInterval(() => {
      if (timeLeft.seconds > 0) {
        setTimeLeft(prevTime => ({
          ...prevTime,
          seconds: prevTime.seconds - 1
        }));
      } else if (timeLeft.minutes > 0) {
        setTimeLeft(prevTime => ({
          ...prevTime,
          minutes: prevTime.minutes - 1,
          seconds: 59
        }));
      } else if (timeLeft.hours > 0) {
        setTimeLeft(prevTime => ({
          ...prevTime,
          hours: prevTime.hours - 1,
          minutes: 59,
          seconds: 59
        }));
      } else if (timeLeft.days > 0) {
        setTimeLeft(prevTime => ({
          ...prevTime,
          days: prevTime.days - 1,
          hours: 23,
          minutes: 59,
          seconds: 59
        }));
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [timeLeft]);

  return (
    <HeroContainer>
      <OverlayGradient />
      <ContentWrapper>
        <HeroHeading>
          Transform Your <HighlightText>Direct Selling Business</HighlightText> With Strategic Consulting
        </HeroHeading>
        
        <Subheading>
          Leverage proven systems, psychology, and advanced technology to scale your direct sales organization and build residual income faster than ever
        </Subheading>
        
        <OfferBadge>
          <BadgeText>EXCLUSIVE OFFER</BadgeText>
        </OfferBadge>
        
        <TimerContainer>
          <TimerHeader>Limited Time: Strategic Business Assessment + Growth Roadmap</TimerHeader>
          <TimerDisplay>
            <TimerUnit>
              <TimerNumber>{timeLeft.days}</TimerNumber>
              <TimerLabel>Days</TimerLabel>
            </TimerUnit>
            <TimerSeparator>:</TimerSeparator>
            <TimerUnit>
              <TimerNumber>{timeLeft.hours.toString().padStart(2, '0')}</TimerNumber>
              <TimerLabel>Hours</TimerLabel>
            </TimerUnit>
            <TimerSeparator>:</TimerSeparator>
            <TimerUnit>
              <TimerNumber>{timeLeft.minutes.toString().padStart(2, '0')}</TimerNumber>
              <TimerLabel>Minutes</TimerLabel>
            </TimerUnit>
            <TimerSeparator>:</TimerSeparator>
            <TimerUnit>
              <TimerNumber>{timeLeft.seconds.toString().padStart(2, '0')}</TimerNumber>
              <TimerLabel>Seconds</TimerLabel>
            </TimerUnit>
          </TimerDisplay>
        </TimerContainer>
        
        <HeroText>
          We've helped over 1,500+ direct selling professionals and network marketing leaders build sustainable teams, automate recruitment, and create reliable systems for lasting success.
        </HeroText>
        
        <CTAContainer>
          <PrimaryButton href="#contact">Book Your Strategy Call</PrimaryButton>
          <SecondaryButton href="#features">Explore Our Services</SecondaryButton>
        </CTAContainer>
        
        <TrustBadges>
          <TrustBadge>
            <TrustBadgeIcon>✓</TrustBadgeIcon>
            <TrustBadgeText>Money-Back Guarantee</TrustBadgeText>
          </TrustBadge>
          <TrustBadge>
            <TrustBadgeIcon>✓</TrustBadgeIcon>
            <TrustBadgeText>No Credit Card Required</TrustBadgeText>
          </TrustBadge>
          <TrustBadge>
            <TrustBadgeIcon>✓</TrustBadgeIcon>
            <TrustBadgeText>24/7 Support</TrustBadgeText>
          </TrustBadge>
        </TrustBadges>
      </ContentWrapper>
      
      <ImageContainer>
        <HeroOverlay />
      </ImageContainer>
    </HeroContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const HeroContainer = styled.div`
  position: relative;
  width: 100%;
  background: linear-gradient(145deg, #1a1a1a 0%, #3a1d54 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 120px 0 80px;
  
  @media (max-width: 991px) {
    padding: 100px 0 60px;
  }
`;

const OverlayGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(43, 8, 61, 0.5) 0%, rgba(81, 0, 135, 0.3) 100%);
  z-index: 1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  max-width: 1240px;
  width: 100%;
  margin: 0 auto;
  padding: 0 30px;
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const HeroHeading = styled.h1`
  font-size: 3.8rem;
  font-weight: 800;
  line-height: 1.2;
  color: white;
  margin-bottom: 25px;
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 991px) {
    font-size: 3.2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.6rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.2rem;
  }
`;

const HighlightText = styled.span`
  color: #D56FEA;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(90deg, rgba(213,111,234,0.3) 0%, rgba(213,111,234,0) 100%);
    z-index: -1;
  }
`;

const Subheading = styled.p`
  font-size: 1.4rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 40px;
  max-width: 80%;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 991px) {
    font-size: 1.2rem;
    max-width: 100%;
  }
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const OfferBadge = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #D56FEA 0%, #9C27B0 100%);
  border-radius: 50px;
  padding: 12px 25px;
  margin-bottom: 30px;
  animation: ${pulse} 2s infinite ease-in-out;
  box-shadow: 0 5px 15px rgba(213, 111, 234, 0.4);
  
  @media (max-width: 768px) {
    padding: 10px 20px;
  }
`;

const BadgeText = styled.span`
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const TimerContainer = styled.div`
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 25px 30px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${fadeIn} 1.2s ease-out;
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const TimerHeader = styled.h3`
  font-size: 1.2rem;
  color: white;
  text-align: center;
  margin-bottom: 20px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TimerDisplay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const TimerUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimerNumber = styled.div`
  background: rgba(0, 0, 0, 0.4);
  color: white;
  font-size: 2.6rem;
  font-weight: 700;
  width: 70px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  margin-bottom: 8px;
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    font-size: 2rem;
    width: 55px;
    height: 65px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    width: 45px;
    height: 55px;
  }
`;

const TimerLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const TimerSeparator = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 2.6rem;
  font-weight: 700;
  margin-top: -25px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const HeroText = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 35px;
  max-width: 80%;
  animation: ${fadeIn} 1.4s ease-out;
  
  @media (max-width: 991px) {
    font-size: 1.1rem;
    max-width: 100%;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  animation: ${fadeIn} 1.6s ease-out;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const PrimaryButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #D56FEA 0%, #9C27B0 100%);
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 16px 30px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(213, 111, 234, 0.3);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 25px rgba(213, 111, 234, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 14px 25px;
    text-align: center;
  }
`;

const SecondaryButton = styled.a`
  display: inline-block;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 16px 30px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 14px 25px;
    text-align: center;
  }
`;

const TrustBadges = styled.div`
  display: flex;
  gap: 30px;
  animation: ${fadeIn} 1.8s ease-out;
  
  @media (max-width: 768px) {
    gap: 15px;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
`;

const TrustBadgeIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: rgba(213, 111, 234, 0.2);
  border-radius: 50%;
  margin-right: 10px;
  color: #D56FEA;
  font-size: 0.8rem;
`;

const TrustBadgeText = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  background-image: url('/images/direct-selling-bg.jpg');
  background-size: cover;
  background-position: center;
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.7) 0%, rgba(58, 29, 84, 0.7) 100%);
`;

export default DirectSellingHero; 